/** Multilayer beam bow from CTE mismatch. SI internally, UI in µm / ppm/K / GPa. */
export function computeWarpage(layers, deltaT, lengthMm = 100) {
  const t = layers.map((l) => Math.max(1, Number(l.tUm) || 1) * 1e-6);
  const E = layers.map((l) => Math.max(0.1, Number(l.eGpa) || 1) * 1e9);
  const a = layers.map((l) => (Number(l.ctePpm) || 0) * 1e-6);
  const H = t.reduce((s, x) => s + x, 0);
  let z = 0;
  const zMid = t.map((th) => {
    const m = z + th / 2;
    z += th;
    return m;
  });
  const EA = E.reduce((s, e, i) => s + e * t[i], 0);
  const zBar = E.reduce((s, e, i) => s + e * t[i] * zMid[i], 0) / EA;
  const EI = E.reduce((s, e, i) => {
    const I = (t[i] ** 3) / 12 + t[i] * (zMid[i] - zBar) ** 2;
    return s + e * I;
  }, 0);
  const dT = Number(deltaT) || 0;
  const M = E.reduce((s, e, i) => s + e * t[i] * a[i] * (zMid[i] - zBar), 0) * dT;
  const kappa = EI > 0 ? M / EI : 0;
  const L = (Number(lengthMm) || 100) / 1000;
  const bowUm = (kappa * L * L) / 8 * 1e6;
  const radiusM = Math.abs(kappa) > 1e-12 ? 1 / Math.abs(kappa) : Infinity;
  const dieIdx = 0;
  const dieStressMpa = (E[dieIdx] * kappa * (zMid[dieIdx] - zBar)) / 1e6;
  const absBow = Math.abs(bowUm);
  const length = Number(lengthMm) || 100;
  let spec = 'ok';
  let specNote = 'Inside a typical wirebond / SMT window for this body size — still check coplanarity at the pads, not only centre bow.';
  if (absBow > 0.0025 * length * 1000) {
    spec = 'fail';
    specNote = 'Centre bow is large versus body size. Expect stencil print, lead coplanarity, or wirebond window issues. Do not ship on a vibe.';
  } else if (absBow > 0.0012 * length * 1000) {
    spec = 'watch';
    specNote = 'In the watch band. Fine for a lab strip; a real map (shadow moiré / confocal) will show corners and die shadow. Measure, do not argue.';
  }
  return {
    kappa,
    bowUm,
    radiusMm: radiusM === Infinity ? Infinity : radiusM * 1e3,
    dieStressMpa,
    zBarUm: zBar * 1e6,
    thicknessUm: H * 1e6,
    concaveToward: bowUm >= 0 ? 'high-CTE face (usually mold / copper)' : 'low-CTE face (usually the die)',
    spec,
    specNote,
  };
}

export const LAYER_COLORS = ['#64748b', '#f59e0b', '#fb923c', '#34d399'];

export const DEFAULT_STACK = [
  { name: 'Si die', tUm: 200, ctePpm: 2.6, eGpa: 160, color: LAYER_COLORS[0] },
  { name: 'Die attach', tUm: 30, ctePpm: 55, eGpa: 4, color: LAYER_COLORS[1] },
  { name: 'Cu leadframe', tUm: 200, ctePpm: 17, eGpa: 120, color: LAYER_COLORS[2] },
  { name: 'EMC mold', tUm: 500, ctePpm: 12, eGpa: 24, color: LAYER_COLORS[3] },
];

export const PRESETS = {
  qfn: {
    label: 'QFN-like',
    deltaT: -155,
    layers: DEFAULT_STACK,
  },
  thickMold: {
    label: 'Thick EMC',
    deltaT: -155,
    layers: [
      { ...DEFAULT_STACK[0] },
      { ...DEFAULT_STACK[1] },
      { ...DEFAULT_STACK[2] },
      { ...DEFAULT_STACK[3], tUm: 900 },
    ],
  },
  thinDie: {
    label: 'Thin die',
    deltaT: -155,
    layers: [
      { ...DEFAULT_STACK[0], tUm: 80 },
      { ...DEFAULT_STACK[1] },
      { ...DEFAULT_STACK[2] },
      { ...DEFAULT_STACK[3] },
    ],
  },
  softAttach: {
    label: 'Soft attach',
    deltaT: -155,
    layers: [
      { ...DEFAULT_STACK[0] },
      { ...DEFAULT_STACK[1], eGpa: 0.8, ctePpm: 80 },
      { ...DEFAULT_STACK[2] },
      { ...DEFAULT_STACK[3] },
    ],
  },
};

export const WARPAGE_KEY = 'qn-sm-warpage';

export function saveWarpageSnapshot(payload) {
  try { sessionStorage.setItem(WARPAGE_KEY, JSON.stringify(payload)); } catch { /* ignore */ }
}

export function loadWarpageSnapshot() {
  try { return JSON.parse(sessionStorage.getItem(WARPAGE_KEY) || 'null'); } catch { return null; }
}
